import { useCallback, useState } from 'react';
import type { IDocumentsSubscribed, IDocumentsSubscribedState } from '../../types';
import { computeAllState, computeHeaderDocumentsSubscribed, getHeaderColDocState } from './helpers';

interface DocumentSubscriptionState {
  documentsSubscribed: IDocumentsSubscribedState;
  headerDocumentsSubscribed: Record<string, boolean>;
  preferredLanguage: string;
}

interface InitParams {
  entries: ReadonlyArray<{ isin: string; documents: any }>;
  documentHeaders: any;
  documentKeys: string[];
  currentLanguageSelection: string;
}

function buildInitialState({ entries, documentHeaders, documentKeys, currentLanguageSelection }: InitParams): DocumentSubscriptionState {
  const documentKeysWithAll = [...documentKeys, 'all'];
  const newDocuments: Record<string, Record<string, unknown>> = {};

  for (const aboTranche of entries) {
    const { __dataID__, all, ...saneDocuments } = aboTranche.documents as any;

    const merged: Record<string, unknown> = {};
    if (documentHeaders.all) {
      for (const key of documentKeys) {
        merged[key] = true;
      }
      merged.all = 1;
    } else {
      for (const key of documentKeys) {
        merged[key] = documentHeaders[key] ? true : saneDocuments[key];
      }
      merged.all = computeAllState(Object.fromEntries(Object.entries(merged).filter(([k]) => k !== 'all')));
    }

    newDocuments[aboTranche.isin] = merged;
  }

  const headerDocs: Record<string, boolean> = {};
  for (const label of documentKeysWithAll) {
    headerDocs[label] = getHeaderColDocState(label, newDocuments) === 1;
  }

  const { __dataID__, ...saneHeaderDocs } = headerDocs as IDocumentsSubscribed & Record<string, unknown>;

  return {
    documentsSubscribed: newDocuments as unknown as IDocumentsSubscribedState,
    headerDocumentsSubscribed: saneHeaderDocs as Record<string, boolean>,
    preferredLanguage: currentLanguageSelection || 'DE',
  };
}

export function useDocumentSubscription(initParams: InitParams) {
  const [state, setState] = useState<DocumentSubscriptionState>(() => buildInitialState(initParams));

  const changeDocumentsSubscription = useCallback((trancheIds: [string], documentTypes: [string], value: boolean) => {
    setState((prev) => {
      const newDocs = { ...prev.documentsSubscribed } as unknown as Record<string, Record<string, unknown>>;

      for (const trancheId of trancheIds) {
        const { all, ...docsWithoutAll } = newDocs[trancheId];

        if (documentTypes.includes('all')) {
          for (const doc of Object.keys(docsWithoutAll)) {
            newDocs[trancheId][doc] = value;
          }
          newDocs[trancheId].all = Number(value);
        } else {
          for (const doc of documentTypes) {
            newDocs[trancheId][doc] = value;
          }
          const { all: _all, ...updated } = newDocs[trancheId];
          newDocs[trancheId].all = computeAllState(updated);
        }
      }

      const newHeader = computeHeaderDocumentsSubscribed(Object.keys(prev.headerDocumentsSubscribed), newDocs);

      return {
        ...prev,
        documentsSubscribed: newDocs as unknown as IDocumentsSubscribedState,
        headerDocumentsSubscribed: newHeader,
      };
    });
  }, []);

  const changePreferredLanguage = useCallback((value: string) => {
    setState((prev) => ({ ...prev, preferredLanguage: value }));
  }, []);

  /** Sync state when relay data changes (entries update from server) */
  const syncFromRelay = useCallback((entries: ReadonlyArray<{ isin: string; documents: any }>) => {
    setState((prev) => {
      const headerKeys = Object.keys(prev.headerDocumentsSubscribed);
      const newDocs: Record<string, Record<string, unknown>> = {};

      for (const aboTranche of entries) {
        const { __dataID__, all, ...saneDocuments } = aboTranche.documents as any;

        if (all) {
          for (const key of Object.keys(saneDocuments)) {
            saneDocuments[key] = true;
          }
          saneDocuments.all = 1;
        } else {
          saneDocuments.all = Object.values(saneDocuments).includes(true) ? 2 : 0;
        }

        newDocs[aboTranche.isin] = saneDocuments;
      }

      const newHeader = computeHeaderDocumentsSubscribed(headerKeys, newDocs);

      return {
        ...prev,
        documentsSubscribed: newDocs as unknown as IDocumentsSubscribedState,
        headerDocumentsSubscribed: newHeader,
      };
    });
  }, []);

  return {
    documentsSubscribed: state.documentsSubscribed,
    headerDocumentsSubscribed: state.headerDocumentsSubscribed,
    preferredLanguage: state.preferredLanguage,
    changeDocumentsSubscription,
    changePreferredLanguage,
    syncFromRelay,
  };
}
